import {
  BanknotesIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  ListBulletIcon,
  TagIcon,
} from '@heroicons/react/24/solid'
import { BarCommonProps } from '@nivo/bar'
import { LineProps, Serie } from '@nivo/line'
import { ReactNode } from 'react'
import { formatCurrencyCompact, formatNumber } from '../../helpers'
import { WidgetPayload } from '../../types'
import { CHART_BOTTOM_AXIS, CHART_LEFT_AXIS, CHART_MARGIN } from './constants'
import {
  ChartType,
  CustomChart,
  DataType,
  ParamType,
  Selectable,
  SpendSeriesPayload,
  TimeSeriesPayload,
  colorSchemes,
} from './types'

const getRenderTickTspanGroups = (
  value: string,
  maxLineLength: number = 15,
  maxLines: number = 2
) => {
  const words = value.split(' ')

  type linesAcc = {
    lines: string[]
    currLine: string
  }

  // reduces the words into lines of maxLineLength
  const assembleLines: linesAcc = words.reduce(
    (acc: linesAcc, word: string) => {
      // if the current line isn't empty and the word + current line is larger than the allowed line size, create a new line and update current line
      if ((word + acc.currLine).length > maxLineLength && acc.currLine !== '') {
        return {
          lines: acc.lines.concat([acc.currLine]),
          currLine: word,
        }
      }
      // otherwise add the word to the current line
      return {
        ...acc,
        currLine: `${acc.currLine} ${word}`,
      }
    },
    { lines: [], currLine: '' }
  )

  // add the ending state of current line (the last line) to lines
  const allLines = assembleLines.lines.concat([assembleLines.currLine])

  // for now, only take first 2 lines due to tick spacing and possible overflow
  const lines = allLines.slice(0, maxLines)
  // eslint-disable-next-line no-undef
  const children: JSX.Element[] = []
  let dy = 0

  lines.forEach((lineText, i) => {
    children.push(
      // eslint-disable-next-line react/no-array-index-key
      <tspan x={0} dy={dy} key={i}>
        {
          // if on the second line, and that line's length is within 3 of the max length, add ellipsis
          i === 1 && allLines.length > 2
            ? `${lineText.slice(0, maxLineLength - 3)}...`
            : lineText
        }
      </tspan>
    )
    // increment dy to render next line text below
    dy += 15
  })

  return children
}

export const sanitiseCustomChart = (cc: Partial<CustomChart>): CustomChart => {
  return {
    dataType: cc.dataType || 'spend-per-month-by-tag-ids',
    chartType: cc.chartType || 'line',
    items: cc.items || [],
    colorScheme: cc.colorScheme || '1',
  }
}

const parseString = (s: string) => {
  try {
    return JSON.parse(s)
  } catch (e) {
    return {}
  }
}

export const parseCustomChart = (cc: string): CustomChart => {
  return sanitiseCustomChart(parseString(cc))
}

export const isValidCustomValue = (cv: string): boolean => {
  const parsed = parseString(cv)
  return (
    parsed.dataType && parsed.chartType && parsed.items && parsed.colorScheme
  )
}

export const filterCustomWidgets = (widget: WidgetPayload): boolean =>
  widget.custom_value !== undefined && isValidCustomValue(widget.custom_value)

export const getParamName = (dataType: DataType): string => {
  switch (dataType) {
    case 'total-spend-by-vendor':
      return 'vendor_ids'
    case 'total-spend-by-tag-category':
      return 'tag_category_ids'
    default:
      return 'tag_ids'
  }
}
export const getParams = (items: Selectable[]): string =>
  items.map((i) => i.id).join(',')

export const chartTypes: {
  id: ChartType
  name: string
  icon: ReactNode
}[] = [
  {
    id: 'line',
    name: 'Line Chart',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-6"
      >
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    ),
  },
  {
    id: 'bar',
    name: 'Bar Chart',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-6"
      >
        <path d="M3 3v18h18" />
        <path d="M18 17V9" />
        <path d="M13 17V5" />
        <path d="M8 17v-3" />
      </svg>
    ),
  },
  // {
  //   id: 'bar-horizontal',
  //   name: 'Horizontal Bar Chart',
  //   icon: (
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       viewBox="0 0 24 24"
  //       fill="none"
  //       stroke="currentColor"
  //       strokeWidth="2"
  //       strokeLinecap="round"
  //       strokeLinejoin="round"
  //       className="h-6"
  //     >
  //       <path d="M3 3v18h18" />
  //       <path d="M7 16h8" />
  //       <path d="M7 11h12" />
  //       <path d="M7 6h3" />
  //     </svg>
  //   ),
  // },
]

export const dataTypes: {
  id: DataType
  name: string
  description: string
  params: ParamType
  icon: ReactNode
  amountType: 'currency' | 'number'
  xLabel: string
  yLabel: string
}[] = [
  {
    id: 'spend-per-month-by-tag-ids',
    name: 'Total Spend Per Month',
    description: 'By Tag ID',
    params: 'tags',
    amountType: 'currency',
    xLabel: 'Month',
    yLabel: 'Spend',
    icon: (
      <div className="relative inline-flex items-center justify-center p-4">
        <div className="text-gray-500 flex items-center justify-center absolute left-1.5 bottom-2 rotate-3">
          <CalendarDaysIcon className="h-5" />
        </div>
        <BanknotesIcon className="h-8 text-gray-400" />
        <div className="text-gray-500 flex items-center justify-center absolute right-1.5 bottom-2 -rotate-6">
          <TagIcon className="h-5" />
        </div>
      </div>
    ),
  },
  {
    id: 'vendors-per-month-by-tag-ids',
    name: 'Vendors Per Month',
    description: 'By Tag ID',
    amountType: 'number',
    params: 'tags',
    xLabel: 'Month',
    yLabel: 'No of Vendors',
    icon: (
      <div className="relative inline-flex items-center justify-center p-4">
        <div className="text-gray-500 flex items-center justify-center absolute left-1.5 bottom-2 rotate-3">
          <BanknotesIcon className="h-5" />
        </div>
        <BuildingStorefrontIcon className="h-8 text-gray-400" />
        <div className="text-gray-500 flex items-center justify-center absolute right-1.5 bottom-2 -rotate-6">
          <TagIcon className="h-5" />
        </div>
      </div>
    ),
  },
  {
    id: 'transactions-per-month-by-tag-ids',
    name: 'Transactions Per Month',
    description: 'By Tag ID',
    params: 'tags',
    amountType: 'number',
    xLabel: 'Month',
    yLabel: 'No of Transactions',
    icon: (
      <div className="relative inline-flex items-center justify-center p-4">
        <div className="text-gray-500 flex items-center justify-center absolute left-1.5 bottom-2 rotate-3">
          <BanknotesIcon className="h-5" />
        </div>
        <ListBulletIcon className="h-8 text-gray-400" />
        <div className="text-gray-500 flex items-center justify-center absolute right-1.5 bottom-2 -rotate-6">
          <TagIcon className="h-5" />
        </div>
      </div>
    ),
  },
  {
    id: 'total-spend-by-vendor',
    name: 'Total Spend by Vendor',
    description: 'By Vendor',
    params: 'vendors',
    amountType: 'currency',
    xLabel: 'Vendor',
    yLabel: 'Total Spend',
    icon: (
      <div className="relative inline-flex items-center justify-center p-4 text-gray-400 -space-x-1">
        <BuildingStorefrontIcon className="h-7 -rotate-6" />
        <BanknotesIcon className="h-7 rotate-6 text-gray-500" />
      </div>
    ),
  },
  {
    id: 'total-spend-by-tag-category',
    name: 'Total Spend',
    amountType: 'currency',
    description: 'By Tag Category',
    params: 'tag_categories',
    xLabel: 'Tag Category',
    yLabel: 'Total Spend',
    icon: (
      <div className="relative inline-flex items-center justify-center p-4 text-gray-400">
        <BanknotesIcon className="h-7 -rotate-6" />
        <TagIcon className="h-7 rotate-6 text-gray-500" />
      </div>
    ),
  },
]

const formatLineSpendSeries = (payload: SpendSeriesPayload): Serie[] => {
  return [
    {
      id: 'formatLineSpendSeries',
      data: payload.map((p) => ({
        x: p.vendor?.name ?? p.category?.name ?? '',
        y: p.amount,
      })),
    },
  ]
}

const formatBarSpendSeries = (payload: SpendSeriesPayload) => {
  return payload.map((p) => {
    const itemName = p.vendor?.name ?? p?.category?.name ?? ''
    const value = p.amount
    return {
      [itemName]: value,
      [`${itemName}_value`]: value,
      [`${itemName}_label`]: itemName,
      id: itemName,
      label: itemName,
      value: p.amount,
      country: p.vendor?.territory?.name ?? '',
    }
  })
}

const formatBarTimeSeries = (payload: TimeSeriesPayload) => {
  const result: { [key: string]: any }[] = []

  payload.forEach((tagObj) => {
    tagObj.data.forEach((dataPoint) => {
      let monthEntry = result.find((entry) => entry?.month === dataPoint.month)
      if (!monthEntry) {
        monthEntry = { month: dataPoint.month }
        result.push(monthEntry)
      }
      const itemName = tagObj.tag_name ?? tagObj.tag ?? ''
      const value = dataPoint.total ?? dataPoint.amount ?? 0

      monthEntry[itemName] = value
      monthEntry.label = itemName
      monthEntry[`${itemName}_value`] = value
      monthEntry[`${itemName}_label`] = itemName
    })
  })

  result.sort(
    (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
  )

  return result.map((r) => ({ ...r, id: r.month }))
}

const formatLineTimeSeries = (payload: TimeSeriesPayload): Serie[] => {
  return payload.map((p) => ({
    id: p.tag_name ?? p.tag ?? p.tag_id ?? '',
    data: p.data.map((d) => ({
      x: d.month,
      y: d.total ?? d.amount ?? 0,
      label: p.tag_name ?? p.tag ?? '',
    })),
  }))
}

export const isMultiSeries = (dataType?: DataType): boolean => {
  return (
    dataType !== 'total-spend-by-tag-category' &&
    dataType !== 'total-spend-by-vendor'
  )
}

const getLabels = (dataType: DataType) => {
  const dt = dataTypes.find((d) => d.id === dataType) ?? dataTypes[0]
  return {
    yLabel: dt.yLabel,
    xLabel: dt.xLabel,
  }
}

const getLabelFormatter = (dataType: DataType) => {
  const dt = dataTypes.find((d) => d.id === dataType) ?? dataTypes[0]
  return dt.amountType === 'currency' ? formatCurrencyCompact : formatNumber
}

export const getBarChartParams = (
  customChartType: CustomChart,
  data: any
): Partial<BarCommonProps<any>> => {
  const isStacked = isMultiSeries(customChartType.dataType)
  const colors = colorSchemes[customChartType.colorScheme]
  const keys = isStacked
    ? data.map((d) => d.tag_name ?? d.tag)
    : data.map((d) => d.vendor?.name ?? d.category?.name ?? '')
  const formattedData = isStacked
    ? formatBarTimeSeries(data)
    : formatBarSpendSeries(data)

  const labels = getLabels(customChartType.dataType)
  const formatter = getLabelFormatter(customChartType.dataType)

  return {
    innerPadding: isStacked ? 2 : 0,
    data: formattedData,
    colorBy: isStacked ? 'id' : 'indexValue',
    margin: CHART_MARGIN,
    colors,
    axisLeft: {
      ...CHART_LEFT_AXIS,
      legend: labels.yLabel,
      format: formatter,
    },
    tooltip: (a) => {
      const label = a?.data?.[`${a?.id}_label`]
      const value = a?.data?.[`${a?.id}_value`]
      return (
        <div className="rounded shadow p-3 bg-white text-sm border border-stone-100 font-medium flex items-center gap-3">
          <div
            className="h-12 w-1 rounded-sm bg-red-100"
            style={{ backgroundColor: a?.color }}
          />
          <div>
            <h3>{label}</h3>
            {value !== undefined && (
              <h4 className="font-semibold">{formatter(value)}</h4>
            )}
          </div>
        </div>
      )
    },
    axisBottom: {
      ...CHART_BOTTOM_AXIS,
      legend: labels.xLabel,
      renderTick: ({
        opacity,
        textAnchor,
        textBaseline,
        textX,
        textY,
        value,
        x,
        y,
      }) => {
        return (
          <g transform={`translate(${textX},${textY})`} style={{ opacity }}>
            <line
              x1="0"
              x2="0"
              y1="0"
              y2="5"
              style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: 1 }}
            />
            <text
              dominantBaseline="hanging"
              alignmentBaseline="baseline"
              textAnchor={textAnchor}
              transform={`translate(${x},${y}) rotate(0)`}
              style={{
                fontFamily: 'sans-serif',
                fontSize: '11px',
                fill: 'rgb(51, 51, 51)',
              }}
            >
              {getRenderTickTspanGroups(value)}
            </text>
          </g>
        )
      },
    },
    ...(keys && { keys }),
  }
}

export const getLineChartParams = (
  customChartType: CustomChart,
  data: any
): Partial<LineProps> => {
  const colors = colorSchemes[customChartType.colorScheme]
  const formattedData = isMultiSeries(customChartType.dataType)
    ? formatLineTimeSeries(data)
    : formatLineSpendSeries(data)
  const labels = getLabels(customChartType.dataType)
  const formatter = getLabelFormatter(customChartType.dataType)
  return {
    data: formattedData,
    margin: CHART_MARGIN,
    colors,
    // @ts-ignore
    axisLeft: {
      ...CHART_LEFT_AXIS,
      legend: labels.yLabel,
      format: formatter,
    },
    tooltip: (a) => {
      const category = a?.point?.serieId
      const month = a?.point?.data?.xFormatted
      const value = a?.point?.data?.y
      return (
        <div className="rounded shadow p-3 bg-white text-sm border border-stone-100 font-medium flex items-center gap-3">
          <div
            className="h-12 w-1 rounded-sm"
            style={{ backgroundColor: a?.point?.serieColor }}
          />
          <div>
            <h3>{category}</h3>
            <h4 className="text-gray-500">{month}</h4>
            {value && <h4 className="font-semibold">{formatter(value)}</h4>}
          </div>
        </div>
      )
    },
    // @ts-ignore
    axisBottom: {
      ...CHART_BOTTOM_AXIS,
      legend: labels.xLabel,
    },
  }
}
